import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ReplaySubject, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { User } from 'src/typings';
import { ProcurementAttentionService } from './procurement-attention.service';

@Component({
  selector: 'app-procurement-attention-shortcut',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *ngIf="view$ | async as view">
      <a mat-stroked-button class="attention needsOrder"
         [class.pending]="(view.summary?.needsOrder?.count || 0) > 0"
         routerLink="/docs/buy/list" [queryParams]="{needs_order: 1, attention: view.summary?.asOf}"
         [matTooltip]="view.needsOrderLabel" [attr.aria-label]="view.needsOrderLabel">
        <mat-icon>add_shopping_cart</mat-icon><span class="count">{{ view.needsOrderBadge }}</span>
      </a>
      <a mat-stroked-button class="attention deliveries"
         [class.pending]="(view.summary?.overdueDeliveries?.count || 0) > 0"
         routerLink="/docs/buy/list" [queryParams]="{overdue: 1, attention: view.summary?.asOf}"
         [matTooltip]="view.deliveryLabel" [attr.aria-label]="view.deliveryLabel">
        <mat-icon>local_shipping</mat-icon>
        <span class="count">{{ view.deliveryBadge }}</span>
      </a>
      <a mat-stroked-button class="attention postOffice"
         [class.pending]="(view.summary?.postOfficeReady?.count || 0) > 0"
         routerLink="/docs/buy/list" [queryParams]="{np_ready: 1, attention: view.summary?.asOf}"
         [matTooltip]="view.postOfficeLabel" [attr.aria-label]="view.postOfficeLabel">
        <mat-icon>markunread_mailbox</mat-icon>
        <span class="count">{{ view.postOfficeBadge }}</span>
      </a>
      <button type="button" mat-stroked-button class="attention minimum"
              [class.pending]="(view.summary?.minimumStock?.count || 0) > 0"
              [matMenuTriggerFor]="warehouses" [matTooltip]="view.stockLabel"
              [attr.aria-label]="view.stockLabel">
        <mat-icon>inventory_2</mat-icon>
        <span class="count">{{ view.stockBadge }}</span>
      </button>
      <mat-menu #warehouses="matMenu">
        <div class="menuLabel">Минимальный остаток · Все склады</div>
        <a mat-menu-item *ngFor="let row of view.summary?.minimumStock?.warehouses"
           routerLink="/procurement/minimum-stock"
           [queryParams]="{warehouseId: row.warehouseId, coverage: 'NEEDED', attention: view.summary?.asOf}">
          <span>{{ row.warehouseName }}</span><span class="warehouseCount">{{ row.count }}</span>
        </a>
        <div class="menuLabel" *ngIf="!view.summary?.minimumStock?.count">{{ view.unavailable
          ? 'Нет связи — счётчик недоступен' : view.summary ? 'Все позиции обеспечены' : 'Загрузка…' }}</div>
        <a mat-menu-item routerLink="/procurement/minimum-stock">Открыть минимальные остатки</a>
      </mat-menu>
      <span class="cdk-visually-hidden" aria-live="polite">{{ view.needsOrderLabel }}. {{ view.deliveryLabel }}. {{ view.stockLabel }}. {{ view.postOfficeLabel }}</span>
    </ng-container>
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; gap: 6px; }
    .attention { display: inline-flex; align-items: center; justify-content: center;
      box-sizing: border-box; height: var(--header-control-height, 38px); line-height: normal;
      padding: 0 8px; min-width: 0;
      border-radius: var(--header-control-radius, 12px); color: #475569; background: #fff; border-color: #dbe4ef;
      font-weight: 400; font-size: 13px; white-space: nowrap; }
    :host ::ng-deep .attention .mat-button-wrapper { display: inline-flex; align-items: center; gap: 6px; }
    .attention mat-icon { width: 18px; height: 18px; font-size: 18px; margin-right: 0; vertical-align: middle; }
    .count { display: inline-block; min-width: 18px; vertical-align: middle; padding: 0 3px;
      border-radius: 5px; line-height: 20px; background: #f1f5f9; font-variant-numeric: tabular-nums; }
    .needsOrder.pending { color: #6d28d9; border-color: #c4b5fd; background: #f5f3ff; }
    .needsOrder.pending .count { background: #ede9fe; font-weight: 600; }
    .deliveries.pending { color: #b91c1c; border-color: #fecaca; background: #fff1f2; }
    .deliveries.pending .count { background: #fee2e2; }
    .postOffice.pending { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
    .postOffice.pending .count { background: #d1fae5; }
    .minimum.pending { color: #92400e; border-color: #fde68a; background: #fffbeb; }
    .minimum.pending .count { background: #fef3c7; }
    .menuLabel { padding: 6px 16px; color: #64748b; font-size: 12px; }
    .warehouseCount { float: right; margin-left: 24px; color: #92400e; }
    @media(max-width: 650px) { :host { grid-column: 1 / -1; } }
  `]
})
export class ProcurementAttentionShortcutComponent {
  private readonly users = new ReplaySubject<User | undefined>(1);
  @Input() set user(value: User | undefined) { this.users.next(value); }
  readonly view$ = this.users.pipe(
    map(user => user && user.isActive !== false && user.roles?.some(role =>
      [role.description, role.normalize_description].some(value =>
        ['user', 'admin'].includes(String(value || '').trim().toLowerCase()))) ? user.id : null),
    distinctUntilChanged(),
    switchMap(userId => userId == null ? of(null) : this.attention.watch().pipe(map(state => {
      const badge = (count: number | undefined) => state.unavailable ? '?' : count == null ? '…' : count;
      const status = state.unavailable ? 'счётчик недоступен' : state.summary ? '' : 'загрузка';
      return { ...state,
        needsOrderBadge: badge(state.summary?.needsOrder?.count),
        needsOrderLabel: `Нужно заказать · Все склады: ${status || state.summary?.needsOrder?.count} заказов поставщикам. Открыть очередь закупщика`,
        postOfficeBadge: badge(state.summary?.postOfficeReady?.count),
        postOfficeLabel: `Готово к получению в Новой Почте · Все склады: ${status || state.summary?.postOfficeReady?.count} заказов. Нажмите, чтобы открыть`,
        deliveryBadge: badge(state.summary?.overdueDeliveries.count),
        stockBadge: badge(state.summary?.minimumStock.count),
        deliveryLabel: `Просроченные поставки · Все склады: ${status || state.summary?.overdueDeliveries.count} заказов с неполученными товарами`,
        stockLabel: `Минимальный остаток · Все склады: ${status || state.summary?.minimumStock.count} позиций к пополнению. Остаток, резерв и ожидаемые поступления учтены`
      };
    })))
  );
  constructor(private readonly attention: ProcurementAttentionService) {}
}
